// Heatmap tracking utility
// Privacy-safe: only tracks coordinates, not content

import { supabase } from './supabase'

interface HeatmapEvent {
  page_path: string
  event_type: 'click' | 'scroll' | 'move'
  x: number
  y: number
  viewport_width: number
  viewport_height: number
  scroll_depth?: number
  device_type: 'mobile' | 'tablet' | 'desktop'
  is_authenticated: boolean
}

class HeatmapTracker {
  private enabled: boolean
  private buffer: HeatmapEvent[] = []
  private flushInterval: number = 30000 // Flush every 30 seconds
  private maxBufferSize: number = 50
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  private lastMoveTime: number = 0
  private moveThrottleMs: number = 1000 // Track moves max once per second

  constructor() {
    this.enabled = import.meta.env.VITE_HEATMAP_ENABLED === 'true'
  }

  private getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const width = window.innerWidth
    if (width < 768) return 'mobile'
    if (width < 1024) return 'tablet'
    return 'desktop'
  }

  private async isAuthenticated(): Promise<boolean> {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    return !!session
  }

  private shouldTrackPage(path: string): boolean {
    // Don't track admin pages or auth pages
    const excludedPaths = ['/admin', '/login', '/signup', '/reset-password']
    return !excludedPaths.some((excluded) => path.startsWith(excluded))
  }

  private async addEvent(event: Omit<HeatmapEvent, 'is_authenticated'>) {
    if (!this.enabled) return

    const isAuth = await this.isAuthenticated()
    this.buffer.push({ ...event, is_authenticated: isAuth })

    // Flush if buffer is full
    if (this.buffer.length >= this.maxBufferSize) {
      await this.flush()
    }
  }

  private async flush() {
    if (this.buffer.length === 0) return

    const events = [...this.buffer]
    this.buffer = []

    try {
      const { error } = await supabase.from('heatmap_events').insert(events)

      if (error) {
        console.error('[Heatmap] Failed to send events:', error)
        // Don't retry - just drop the events to avoid memory buildup
      }
    } catch (error) {
      console.error('[Heatmap] Error flushing events:', error)
    }
  }

  public init() {
    if (!this.enabled) {
      // console.log('[Heatmap] Tracking disabled');
      return
    }

    const pagePath = window.location.pathname
    if (!this.shouldTrackPage(pagePath)) {
      // console.log('[Heatmap] Skipping tracking for:', pagePath);
      return
    }

    // console.log('[Heatmap] Initializing tracking for:', pagePath);

    // Track clicks
    document.addEventListener(
      'click',
      (e) => {
        this.addEvent({
          page_path: window.location.pathname,
          event_type: 'click',
          x: Math.round(e.clientX),
          y: Math.round(e.clientY),
          viewport_width: Math.round(window.innerWidth),
          viewport_height: Math.round(window.innerHeight),
          scroll_depth: Math.round(window.scrollY),
          device_type: this.getDeviceType(),
        })
      },
      { passive: true },
    )

    // Track scroll (throttled)
    let scrollTimeout: ReturnType<typeof setTimeout>
    document.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollTimeout)
        scrollTimeout = setTimeout(() => {
          this.addEvent({
            page_path: window.location.pathname,
            event_type: 'scroll',
            x: Math.round(window.scrollX),
            y: Math.round(window.scrollY),
            viewport_width: Math.round(window.innerWidth),
            viewport_height: Math.round(window.innerHeight),
            scroll_depth: Math.round(window.scrollY),
            device_type: this.getDeviceType(),
          })
        }, 500)
      },
      { passive: true },
    )

    // Track mouse movement (heavily throttled)
    document.addEventListener(
      'mousemove',
      (e) => {
        const now = Date.now()
        if (now - this.lastMoveTime < this.moveThrottleMs) return

        this.lastMoveTime = now
        this.addEvent({
          page_path: window.location.pathname,
          event_type: 'move',
          x: Math.round(e.clientX),
          y: Math.round(e.clientY),
          viewport_width: Math.round(window.innerWidth),
          viewport_height: Math.round(window.innerHeight),
          scroll_depth: Math.round(window.scrollY),
          device_type: this.getDeviceType(),
        })
      },
      { passive: true },
    )

    // Setup auto-flush
    this.flushTimer = setInterval(() => {
      this.flush()
    }, this.flushInterval)

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flush()
    })

    // Flush on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush()
      }
    })
  }

  public destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
    this.flush() // Final flush
  }
}

// Export singleton
export const heatmapTracker = new HeatmapTracker()
